/**
 * DoaSetCard — surfaces when the cascade engine flags 'doa'
 * (DOA / pronouncement / death-on-scene).
 *
 * Captures pronouncement criteria + pronouncement time. Triggers
 * automatic ME referral packet.
 */

import type { EPCR } from '@/types';
import { SetCard, LabeledRow, ChipSelect } from './SetCard';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

type DoaFindings = {
  pronounced_at?: string;
  pronouncing_provider?: string;
  obvious_death_criteria?: string[];   // rigor, lividity, decap, decomp, incineration
  resuscitation_attempted?: 'yes' | 'no' | 'terminated';
  termination_authorized_by?: string;
  notification_complete?: boolean;
};

const CRITERIA = [
  'rigor mortis',
  'dependent lividity',
  'decapitation',
  'evident decomposition',
  'transection',
  'burned beyond recognition',
  'valid DNR / POLST',
];

export function DoaSetCard({ epcr, onUpdate }: Props) {
  const f = ((epcr.set_findings ?? {}).doa ?? {}) as DoaFindings;
  const update = (patch: Partial<DoaFindings>) => {
    onUpdate({
      set_findings: {
        ...(epcr.set_findings ?? {}),
        doa: { ...f, ...patch },
      },
    });
  };
  const locked = epcr.locked;

  return (
    <SetCard
      title="Death on Scene / DOA Set"
      triggeredBy="Patient pronounced or resuscitation terminated. RI Protocol pronouncement criteria + ME referral required."
      feeds={['nemsis_xml', 'me_referral', 'serious_unusual', 'narrative_engine']}
      accent="rose"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LabeledRow label="Pronouncement Time">
          <input
            type="datetime-local"
            disabled={locked}
            value={f.pronounced_at ?? ''}
            onChange={(e) => update({ pronounced_at: e.target.value })}
            className="rounded border border-slate-300 px-3 py-2 text-sm min-h-touch focus:border-blue-500 focus:outline-none"
          />
        </LabeledRow>
        <LabeledRow label="Pronouncing Provider" hint="Name + role">
          <input
            type="text"
            disabled={locked}
            value={f.pronouncing_provider ?? ''}
            onChange={(e) => update({ pronouncing_provider: e.target.value })}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm min-h-touch focus:border-blue-500 focus:outline-none"
          />
        </LabeledRow>
      </div>

      <LabeledRow label="Obvious Death Criteria" hint="Multi-select; only one needed for field pronouncement.">
        <ChipSelect
          options={CRITERIA}
          value={f.obvious_death_criteria ?? []}
          disabled={locked}
          multi
          onChange={(v) => update({ obvious_death_criteria: v as string[] })}
        />
      </LabeledRow>

      <LabeledRow label="Resuscitation">
        <ChipSelect
          options={['yes', 'no', 'terminated']}
          value={f.resuscitation_attempted ?? null}
          disabled={locked}
          onChange={(v) => update({ resuscitation_attempted: v as DoaFindings['resuscitation_attempted'] })}
        />
      </LabeledRow>

      {f.resuscitation_attempted === 'terminated' && (
        <LabeledRow label="Termination Authorized By" hint="Online MD name + contact time">
          <input
            type="text"
            disabled={locked}
            value={f.termination_authorized_by ?? ''}
            onChange={(e) => update({ termination_authorized_by: e.target.value })}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm min-h-touch focus:border-blue-500 focus:outline-none"
          />
        </LabeledRow>
      )}

      <LabeledRow label="ME / next-of-kin notification complete?">
        <ChipSelect
          options={['yes', 'no']}
          value={f.notification_complete === true ? 'yes' : f.notification_complete === false ? 'no' : null}
          disabled={locked}
          onChange={(v) => update({ notification_complete: v === 'yes' ? true : v === 'no' ? false : undefined })}
        />
      </LabeledRow>
    </SetCard>
  );
}
